import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DataTable,
  DateRangeFilter,
  ExportButtons,
  FilterBar,
  Pager,
  Pill,
  SearchInput,
  Section,
  SelectFilter,
  StatCard,
  StatusBadge,
  usePaged,
} from "@/components/admin/admin-kit";
import {
  admCreatePaymentLink,
  admPaymentEvents,
  admPaymentLinks,
  admSetPaymentLinkActive,
  admStripePayments,
  admStripeRefund,
} from "@/lib/admin-enterprise.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/payments")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/payments",
      "Payments | Control room",
      "Internal administration.",
    ),
  component: AdminPayments,
});

const TABS = ["charges", "payment links", "webhooks"] as const;

function AdminPayments() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("charges");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <Pill key={t} active={tab === t} onClick={() => setTab(t)}>
            {t}
          </Pill>
        ))}
      </div>
      {tab === "charges" ? <ChargesTab /> : tab === "payment links" ? <LinksTab /> : <WebhooksTab />}
    </div>
  );
}

function LinksTab() {
  const qc = useQueryClient();
  const list = useServerFn(admPaymentLinks);
  const create = useServerFn(admCreatePaymentLink);
  const setActive = useServerFn(admSetPaymentLinkActive);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    name: "",
    amount: "",
    currency: "AED",
    description: "",
    reference: "",
    redirectUrl: "",
    quantityAdjustable: false,
    collectAddress: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["adm-payment-links"],
    queryFn: () => list({ data: { limit: 100 } }),
  });
  const all = data?.rows ?? [];
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? all.filter((r) => JSON.stringify(r).toLowerCase().includes(s)) : all;
  }, [all, search]);
  const paged = usePaged(rows, 50);

  const createMut = useMutation({
    mutationFn: () => {
      const cents = Math.round(Number(form.amount) * 100);
      if (!form.name.trim() || !Number.isFinite(cents) || cents < 100) {
        throw new Error("Enter a product name and an amount of at least 1.00.");
      }
      return create({
        data: {
          name: form.name.trim(),
          amountCents: cents,
          currency: form.currency.trim().toLowerCase() || "aed",
          quantityAdjustable: form.quantityAdjustable,
          collectAddress: form.collectAddress,
          ...(form.description.trim() ? { description: form.description.trim() } : {}),
          ...(form.reference.trim() ? { reference: form.reference.trim() } : {}),
          ...(form.redirectUrl.trim() ? { redirectUrl: form.redirectUrl.trim() } : {}),
        },
      });
    },
    onSuccess: async (res) => {
      await navigator.clipboard?.writeText(res.url).catch(() => undefined);
      toast.success("Payment link created — URL copied to clipboard.");
      setForm((f) => ({ ...f, name: "", amount: "", description: "", reference: "" }));
      void qc.invalidateQueries({ queryKey: ["adm-payment-links"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not create the link."),
  });

  const toggleMut = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => setActive({ data: input }),
    onSuccess: () => {
      toast.success("Payment link updated.");
      void qc.invalidateQueries({ queryKey: ["adm-payment-links"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const exportRows = rows.map((r) => ({
    id: r.id,
    product: r.product,
    amount: (r.amount_cents / 100).toFixed(2),
    currency: r.currency,
    active: r.active ? "yes" : "no",
    reference: r.reference ?? "",
    url: r.url,
  }));

  return (
    <div className="space-y-4">
      <Section
        title="Create a payment link"
        description="Generate a hosted Stripe checkout link manually — share it by email, chat or invoice."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Product / description</label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Custom promotion package"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Amount</label>
              <Input
                value={form.amount}
                inputMode="decimal"
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="250.00"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Currency</label>
              <Input
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
                maxLength={3}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Internal reference (optional)</label>
            <Input
              value={form.reference}
              onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
              placeholder="Order, user handle or note"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Redirect after payment (optional)</label>
            <Input
              value={form.redirectUrl}
              onChange={(e) => setForm((f) => ({ ...f, redirectUrl: e.target.value }))}
              placeholder="https://behot.now/thank-you"
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Checkout note (optional)</label>
            <Input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Shown to the payer on the Stripe page"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.quantityAdjustable}
              onChange={(e) => setForm((f) => ({ ...f, quantityAdjustable: e.target.checked }))}
            />
            Let the payer change quantity
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.collectAddress}
              onChange={(e) => setForm((f) => ({ ...f, collectAddress: e.target.checked }))}
            />
            Collect billing address
          </label>
          <Button size="sm" disabled={createMut.isPending} onClick={() => createMut.mutate()}>
            {createMut.isPending ? "Creating…" : "Create payment link"}
          </Button>
        </div>
      </Section>

      <Section
        title="Payment links"
        description="All manually created Stripe payment links."
        actions={<ExportButtons rows={exportRows} filename="behot-payment-links" title="Payment links" />}
      >
        {data?.error && (
          <p className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">
            {data.error}
          </p>
        )}
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Product, reference or link id" />
        </FilterBar>
        {isLoading ? (
          <p className="py-6 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <DataTable
              rows={paged.slice}
              rowKey={(r) => r.id}
              empty="No payment links yet."
              columns={[
                {
                  key: "product",
                  header: "Product",
                  sortValue: (r) => r.product,
                  cell: (r) => (
                    <div>
                      <p className="font-medium">{r.product}</p>
                      {r.reference && (
                        <p className="text-xs text-muted-foreground">Ref: {r.reference}</p>
                      )}
                    </div>
                  ),
                },
                {
                  key: "amount",
                  header: "Amount",
                  sortValue: (r) => r.amount_cents,
                  cell: (r) => money(r.amount_cents, r.currency),
                },
                {
                  key: "status",
                  header: "Status",
                  cell: (r) => <StatusBadge status={r.active ? "active" : "inactive"} />,
                },
                {
                  key: "url",
                  header: "Link",
                  cell: (r) => (
                    <div className="flex items-center gap-2">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-xs text-primary underline"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          void navigator.clipboard?.writeText(r.url);
                          toast.success("Link copied.");
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  ),
                },
                {
                  key: "actions",
                  header: "",
                  cell: (r) => (
                    <Button
                      size="sm"
                      variant={r.active ? "outline" : "default"}
                      disabled={toggleMut.isPending}
                      onClick={() => toggleMut.mutate({ id: r.id, active: !r.active })}
                    >
                      {r.active ? "Deactivate" : "Activate"}
                    </Button>
                  ),
                },
              ]}
            />
            <Pager {...paged} onPrev={paged.prev} onNext={paged.next} />
          </>
        )}
      </Section>
    </div>
  );
}


function money(cents: number, currency: string) {
  return `${currency} ${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
}

function ChargesTab() {
  const qc = useQueryClient();
  const list = useServerFn(admStripePayments);
  const refund = useServerFn(admStripeRefund);
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [refunding, setRefunding] = useState<string | null>(null);
  const [amount, setAmount] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["adm-stripe", applied, status, range.from, range.to],
    queryFn: () =>
      list({
        data: {
          ...(applied ? { search: applied } : {}),
          ...(status !== "all" ? { status } : {}),
          ...(range.from ? { from: range.from } : {}),
          ...(range.to ? { to: range.to } : {}),
          limit: 100,
        },
      }),
  });

  const rows = data?.rows ?? [];

  const mut = useMutation({
    mutationFn: (input: { chargeId: string; amountCents?: number }) => refund({ data: input }),
    onSuccess: () => {
      toast.success("Refund created in Stripe.");
      setRefunding(null);
      setAmount("");
      void qc.invalidateQueries({ queryKey: ["adm-stripe"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Refund failed."),
  });

  const exportRows = rows.map((r) => ({
    date: r.created.slice(0, 10),
    charge: r.id,
    payment_intent: r.payment_intent ?? "",
    email: r.email ?? "",
    card: r.card ?? "",
    amount: (r.amount / 100).toFixed(2),
    refunded: (r.amount_refunded / 100).toFixed(2),
    currency: r.currency,
    status: r.status,
    order_id: r.order_id ?? "",
  }));

  const paged = usePaged(rows, 50);
  const gross = rows.reduce((s, r) => s + r.amount, 0);
  const refunded = rows.reduce((s, r) => s + r.amount_refunded, 0);

  return (
    <Section
      title="Stripe payments"
      description="Search by charge, payment intent, refund id, email or order id — then refund in one click."
      actions={<ExportButtons rows={exportRows} filename="behot-stripe-charges" title="Stripe charges" />}
    >
      {data?.error && (
        <p className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">
          {data.error}
        </p>
      )}
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatCard label="Charges" value={rows.length} />
        <StatCard label="Gross" value={money(gross, rows[0]?.currency ?? "AED")} tone="ok" />
        <StatCard label="Refunded" value={money(refunded, rows[0]?.currency ?? "AED")} tone="warn" />
      </div>
      <FilterBar>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="ch_… / pi_… / re_… / email / order id"
        />
        <Button size="sm" variant="outline" onClick={() => setApplied(search.trim())}>
          Search
        </Button>
        <SelectFilter
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: "all", label: "All" },
            { value: "succeeded", label: "succeeded" },
            { value: "refunded", label: "refunded" },
            { value: "pending", label: "pending" },
            { value: "failed", label: "failed" },
          ]}
        />
        <DateRangeFilter from={range.from} to={range.to} onChange={setRange} />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading Stripe…</p>
      ) : (
        <>
          <DataTable
            rows={paged.slice}
            rowKey={(r) => r.id}
            empty="No Stripe charges for these filters."
            columns={[
              {
                key: "date",
                header: "Date",
                sortValue: (r) => r.created,
                cell: (r) => new Date(r.created).toLocaleString(),
              },
              {
                key: "charge",
                header: "Charge",
                cell: (r) => (
                  <div>
                    <p className="font-mono text-xs">{r.id}</p>
                    <p className="text-xs text-muted-foreground">{r.card ?? r.email ?? "—"}</p>
                  </div>
                ),
              },
              {
                key: "amount",
                header: "Amount",
                sortValue: (r) => r.amount,
                cell: (r) => (
                  <span className="whitespace-nowrap">
                    {money(r.amount, r.currency)}
                    {r.amount_refunded > 0 && (
                      <span className="ml-1 text-xs text-red-600">
                        −{money(r.amount_refunded, r.currency)}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                sortValue: (r) => r.status,
                cell: (r) => (
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={r.status} />
                    {r.dispute && <StatusBadge status="disputed" />}
                  </div>
                ),
              },
              {
                key: "actions",
                header: "",
                cell: (r) => (
                  <div className="flex items-center gap-1.5">
                    {r.receipt_url && (
                      <a
                        href={r.receipt_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Open Stripe receipt"
                      >
                        <ExternalLink className="size-4" />
                      </a>
                    )}
                    {refunding === r.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          className="h-8 w-24"
                          placeholder="Full"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                        />
                        <Button
                          size="sm"
                          variant="hot"
                          disabled={mut.isPending}
                          onClick={() => {
                            const cents = Math.round(Number(amount) * 100);
                            mut.mutate({
                              chargeId: r.id,
                              ...(amount && cents > 0 ? { amountCents: cents } : {}),
                            });
                          }}
                        >
                          Confirm
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRefunding(null)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={r.refunded}
                        onClick={() => setRefunding(r.id)}
                      >
                        Refund
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
          <Pager {...paged} onPrev={paged.prev} onNext={paged.next} />
        </>
      )}
    </Section>
  );
}

function WebhooksTab() {
  const list = useServerFn(admPaymentEvents);
  const [search, setSearch] = useState("");
  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-payment-events"],
    queryFn: () => list({ data: { limit: 300 } }),
  });

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return data;
    return data.filter((r) => JSON.stringify(r).toLowerCase().includes(s));
  }, [data, search]);

  const exportRows = rows.map((r) => ({
    created: String(r["created_at"] ?? ""),
    type: String(r["type"] ?? ""),
    event_id: String(r["stripe_event_id"] ?? ""),
  }));

  const paged = usePaged(rows, 50);

  return (
    <Section
      title="Payment webhook log"
      description="Every Stripe event received by the platform."
      actions={<ExportButtons rows={exportRows} filename="behot-payment-events" title="Payment events" />}
    >
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Event type or id" />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <DataTable
            rows={paged.slice}
            rowKey={(r) => String(r["id"] ?? r["stripe_event_id"])}
            empty="No webhook events."
            columns={[
              {
                key: "created",
                header: "Received",
                sortValue: (r) => String(r["created_at"] ?? ""),
                cell: (r) => new Date(String(r["created_at"])).toLocaleString(),
              },
              {
                key: "type",
                header: "Type",
                sortValue: (r) => String(r["type"] ?? ""),
                cell: (r) => <span className="font-mono text-xs">{String(r["type"] ?? "—")}</span>,
              },
              {
                key: "event",
                header: "Stripe event",
                cell: (r) => (
                  <span className="font-mono text-xs text-muted-foreground">
                    {String(r["stripe_event_id"] ?? "—")}
                  </span>
                ),
              },
            ]}
          />
          <Pager {...paged} onPrev={paged.prev} onNext={paged.next} />
        </>
      )}
    </Section>
  );
}
